import type { schedule as SCH } from '@cwos/api-client';

/**
 * Scheduling screen logic — presentation and form validation only. Pure
 * functions, unit-tested; no react-query/router imports here.
 *
 * The shift CRUD contract (`POST/PATCH/DELETE /schedule/shifts*`) has existed
 * since Task 11 with no Admin form behind it: the screen could only READ the
 * plan, never create, edit, or cancel it — a gap directly against the
 * founder's P0-3 acceptance criterion ("ADMIN can create shifts/assignments,
 * assign workers, edit assignments, cancel assignments"). This module is the
 * draft/validate/payload plumbing for the create and edit forms that close it.
 */

export interface DraftErrors {
  [field: string]: string;
}

export interface ShiftDraft {
  workerId: string;
  programId: string;
  locationId: string;
  /** `datetime-local` input value, in the viewer's own local time. */
  startsAt: string;
  /** `datetime-local` input value, in the viewer's own local time. */
  endsAt: string;
  status: 'scheduled' | 'confirmed';
  notes: string;
}

/**
 * The current moment as a `datetime-local` input value, in the VIEWER's own
 * local time — the picker itself is local, so the default it opens with has
 * to be too, or it would look wrong the instant it rendered.
 */
export function nowLocalInput(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/** An ISO instant, as the same `datetime-local` shape, for pre-filling an edit. */
export function toDatetimeLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return nowLocalInput(d);
}

/** A blank create draft. `workerId` is required and left for the picker. */
export function emptyShiftDraft(now: Date): ShiftDraft {
  const start = new Date(now.getTime() + 60 * 60 * 1000); // an hour from now
  const end = new Date(start.getTime() + 4 * 60 * 60 * 1000); // a 4-hour shift
  return {
    workerId: '',
    programId: '',
    locationId: '',
    startsAt: nowLocalInput(start),
    endsAt: nowLocalInput(end),
    status: 'scheduled',
    notes: '',
  };
}

/** Pre-fills an edit draft from an existing shift (worker is fixed, not editable). */
export function shiftDraftFromShift(shift: SCH.Shift): ShiftDraft {
  return {
    workerId: shift.workerId,
    programId: shift.programId ?? '',
    locationId: shift.locationId ?? '',
    startsAt: toDatetimeLocalInput(shift.startsAt),
    endsAt: toDatetimeLocalInput(shift.endsAt),
    status: shift.status === 'confirmed' ? 'confirmed' : 'scheduled',
    notes: shift.notes ?? '',
  };
}

/**
 * Validates a shift draft. `nowIso` is a courtesy client-side floor; the
 * server is the last word regardless.
 */
export function validateShiftDraft(
  draft: ShiftDraft,
  opts: { requireWorker: boolean },
): DraftErrors {
  const errors: DraftErrors = {};
  if (opts.requireWorker && draft.workerId.trim() === '') {
    errors['workerId'] = 'Choose who this shift is for.';
  }
  if (draft.startsAt.trim() === '') {
    errors['startsAt'] = 'Choose when the shift starts.';
  }
  if (draft.endsAt.trim() === '') {
    errors['endsAt'] = 'Choose when the shift ends.';
  }
  if (draft.startsAt && draft.endsAt) {
    const start = new Date(draft.startsAt);
    const end = new Date(draft.endsAt);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      errors['startsAt'] = errors['startsAt'] ?? 'That date and time is not valid.';
    } else if (end.getTime() <= start.getTime()) {
      errors['endsAt'] = 'The shift must end after it starts.';
    }
  }
  if (draft.notes.length > 2000) {
    errors['notes'] = 'Keep the note under 2,000 characters.';
  }
  return errors;
}

/** Builds the `POST /schedule/shifts` body from a validated create draft. */
export function shiftCreatePayload(draft: ShiftDraft): SCH.CreateShiftInput {
  return {
    workerId: draft.workerId,
    programId: draft.programId || undefined,
    locationId: draft.locationId || undefined,
    startsAt: new Date(draft.startsAt).toISOString(),
    endsAt: new Date(draft.endsAt).toISOString(),
    status: draft.status,
    notes: draft.notes.trim() || undefined,
  };
}

/**
 * Builds the `PATCH /schedule/shifts/:id` body from an edit draft. Worker is
 * never included — reassigning to a different worker is a new shift, not an
 * edit of this one (the API has no such field to send).
 */
export function shiftUpdatePayload(draft: ShiftDraft): SCH.ShiftPatch {
  return {
    programId: draft.programId || null,
    locationId: draft.locationId || null,
    startsAt: new Date(draft.startsAt).toISOString(),
    endsAt: new Date(draft.endsAt).toISOString(),
    notes: draft.notes.trim() || null,
  };
}

/** The server's message when it has one — a 409 conflict is usually specific. */
export function shiftErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message.trim() !== '') return err.message;
  return fallback;
}

/**
 * The shifts that started with nobody clocked in.
 *
 * Extracted from `Scheduling.tsx` when the coverage view stopped fetching a
 * week per worker. It used to be a loop over N query results; it is now a
 * filter over the one list the screen already had, and it is a pure function so
 * the three rules inside it are testable without a browser:
 *
 *   - a shift with a session is covered, whatever its status;
 *   - a cancelled shift is not a gap, it is a shift that was called off;
 *   - a shift that has not STARTED yet is not a gap either, it is the future.
 *     This is the rule most easily lost in a rewrite, and losing it turns every
 *     future shift on the roster into an alarm.
 */
export interface CoverageShift {
  id: string;
  workerId: string;
  startsAt: string;
  endsAt: string;
  status: string;
  session: { id: string; status: string } | null;
}

export function coverageGaps<T extends CoverageShift>(shifts: readonly T[], now: Date): T[] {
  const cutoff = now.getTime();
  return shifts
    .filter((s) => {
      if (s.session) return false;
      if (s.status === 'cancelled') return false;
      return new Date(s.startsAt).getTime() <= cutoff;
    })
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt) || a.id.localeCompare(b.id));
}
